import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { todayIso, fmtDateTimeShort } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Front Desk Board — dense single-viewport operational view.
 *
 * Sits alongside Command Center; does NOT replace it. Operators who prefer the
 * legacy HMS-style rack + KPI tiles + 4 transaction grids use this screen;
 * everyone else keeps Command Center.
 *
 * Layout fits a typical 20-room property on 1366x768 with no scroll. Larger
 * properties get auto-shrinking tiles; very large properties fall back to
 * an internal scroll on the rack region only — the page itself never scrolls.
 */

const AUTO_REFRESH_MS = 30_000;

const STATUS_COLOR = {
  OCCUPIED:  { bg: '#cfe2ff', border: '#1d4ed8', text: '#1e3a8a' }, // cyan
  RESERVED:  { bg: '#fef3c7', border: '#b45309', text: '#92400e' }, // amber
  VACANT:    { bg: '#dcfce7', border: '#15803d', text: '#14532d' }, // green
  DIRTY:     { bg: '#fee2e2', border: '#b91c1c', text: '#7f1d1d' }, // red
  BLOCKED:   { bg: '#e5e7eb', border: '#6b7280', text: '#374151' }, // grey
  OOS:       { bg: '#fde68a', border: '#a16207', text: '#713f12' }, // yellow
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const shortGuestName = (name) => {
  const s = String(name || '').trim();
  if (s.length <= 16) return s;
  return s.slice(0, 14) + '…';
};

const resolveTileStatus = (room) => {
  if (room.occupancy_status === 'OCCUPIED') return 'OCCUPIED';
  if (room.occupancy_status === 'RESERVED') return 'RESERVED';
  if (String(room.maintenance_status || '').toUpperCase() === 'OOS') return 'OOS';
  if (['OOO', 'BLOCKED'].includes(String(room.maintenance_status || '').toUpperCase())) return 'BLOCKED';
  if (String(room.housekeeping_status || '').toUpperCase() === 'DIRTY') return 'DIRTY';
  return 'VACANT';
};

const isLateCheckout = (room) => {
  if (room.occupancy_status !== 'OCCUPIED') return false;
  if (!room.departure_date) return false;
  return String(room.departure_date) < todayIso();
};

function PmsFrontDeskBoard({ token, selectedProperty, onOpenWalkIn, onEditReservation, onNewWalkIn, onNewReservation }) {
  const { isMobile } = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [density, setDensity] = useState(() => localStorage.getItem('fdb_density') || 'normal');
  const [search, setSearch] = useState('');

  const refreshTimer = useRef(null);
  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const loadBoard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/pms/front-desk/board?_=${Date.now()}`, {
        headers: { ...headers(), 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const json = await res.json();
      setData(json);
      setLastRefreshedAt(new Date());
    } catch (err) {
      setError(err.message || 'Failed to load board');
    } finally {
      setLoading(false);
    }
  }, [token, headers]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard, selectedProperty?.id]);

  useEffect(() => {
    if (refreshTimer.current) clearInterval(refreshTimer.current);
    refreshTimer.current = setInterval(() => {
      if (!document.hidden) loadBoard();
    }, AUTO_REFRESH_MS);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [loadBoard]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        document.getElementById('fdb-search')?.focus();
      } else if (e.key.toLowerCase() === 'r' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        loadBoard();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loadBoard]);

  const toggleDensity = () => {
    const next = density === 'compact' ? 'normal' : 'compact';
    setDensity(next);
    localStorage.setItem('fdb_density', next);
  };

  const kpis = data?.kpis || {};
  const rack = data?.rack || [];
  const debtors = data?.debtors || [];
  const roomContinue = data?.room_continue || [];
  const yesterdayArrivals = data?.yesterday_arrivals || [];
  const todayArrivals = data?.today_arrivals || [];
  const todayDepartures = data?.today_departures || [];

  // Filter rack by quick search (room number / guest name / mobile last 4 not in payload, skip)
  const filteredRack = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rack;
    return rack.filter(r =>
      String(r.room_number || '').toLowerCase().includes(q) ||
      String(r.primary_guest_name || '').toLowerCase().includes(q)
    );
  }, [rack, search]);

  // Group rack by floor for visual rows
  const rackByFloor = useMemo(() => {
    const map = new Map();
    filteredRack.forEach(r => {
      const f = String(r.floor_no || '—');
      if (!map.has(f)) map.set(f, []);
      map.get(f).push(r);
    });
    const sorted = Array.from(map.entries()).sort(([a], [b]) => {
      const na = Number(a), nb = Number(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
    return sorted;
  }, [filteredRack]);

  // Auto-fit column count: 5 cols for small floors, scale up for bigger
  const maxRoomsPerFloor = useMemo(() => {
    return rackByFloor.reduce((m, [, rooms]) => Math.max(m, rooms.length), 5);
  }, [rackByFloor]);
  const rackCols = Math.max(5, Math.min(8, maxRoomsPerFloor));

  const handleTileClick = (room) => {
    if (room.reservation_id) {
      onEditReservation?.(room.reservation_id);
    } else {
      onOpenWalkIn?.(room);
    }
  };

  const kpiTiles = [
    { label: 'Vacant',          value: kpis.vacant ?? 0,           color: '#15803d' },
    { label: 'Occupied',        value: `${kpis.occupied ?? 0}/${kpis.total_rooms ?? 0}`, color: '#1d4ed8' },
    { label: 'Dirty',           value: kpis.dirty ?? 0,            color: '#b91c1c' },
    { label: 'Blocked',         value: kpis.blocked ?? 0,          color: '#6b7280' },
    { label: 'Occupied %',      value: `${kpis.occupancy_pct ?? 0}`, color: '#0e7490' },
    { label: 'Total Pax',       value: kpis.total_pax ?? 0,        color: '#0e7490' },
    { label: 'Extra Bed',       value: kpis.extra_bed ?? 0,        color: '#0e7490' },
    { label: 'Inhouse Foreign', value: kpis.inhouse_foreign ?? 0,  color: '#0e7490' },
    { label: 'Today Arrivals',  value: kpis.today_arrivals ?? 0,   color: '#7c3aed' },
    { label: 'Today Departures',value: kpis.today_departures ?? 0, color: '#7c3aed' },
    { label: 'Walkin Arrivals', value: kpis.walkin_arrivals ?? 0,  color: '#7c3aed' },
    { label: 'Day Use',         value: kpis.day_use ?? 0,          color: '#7c3aed' },
  ];

  const tileH = density === 'compact' ? 54 : 74;
  const tileFont = density === 'compact' ? 10 : 11;
  const tileNumberFont = density === 'compact' ? 14 : 16;
  const gridRowFont = density === 'compact' ? 11 : 12;

  return (
    <div style={isMobile ? { ...S.root, height: 'auto', overflow: 'visible' } : S.root}>
      {/* ── Top banner ── */}
      <div style={isMobile ? { ...S.banner, height: 'auto', minHeight: 36, flexWrap: 'wrap', gap: 6, padding: '6px 12px' } : S.banner}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {selectedProperty?.name || 'Front Desk Board'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <input
            id="fdb-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search room / guest  (press /)"
            style={S.search}
          />
          <button onClick={toggleDensity} style={S.btn}>
            {density === 'compact' ? '▢ Normal' : '▣ Compact'}
          </button>
          <button onClick={() => onNewWalkIn?.()} style={S.btnPrimary}>+ Walk-In</button>
          <button onClick={() => onNewReservation?.()} style={S.btnPrimary}>+ Reservation</button>
          <button onClick={loadBoard} style={S.btn} disabled={loading}>
            {loading ? '↻ …' : '↻ Refresh'}
          </button>
          <span style={S.refreshHint}>
            {lastRefreshedAt
              ? `Last: ${lastRefreshedAt.toLocaleTimeString('en-IN', { hour12: false })}`
              : ''}
          </span>
        </div>
      </div>

      {error && <div style={S.errorBar}>{error}</div>}

      {/* ── Top half: Rack (left) + KPIs+Debtors (right) ── */}
      <div style={isMobile ? { ...S.topRow, flexDirection: 'column', flex: 'none' } : S.topRow}>
        {/* Rack */}
        <div style={isMobile ? { ...S.rackPanel, width: '100%' } : S.rackPanel}>
          <div style={S.panelHeader}>Room Rack</div>
          <div style={S.rackBody}>
            {rackByFloor.length === 0 && !loading && (
              <div style={S.emptyHint}>No rooms to show</div>
            )}
            {rackByFloor.map(([floor, rooms]) => (
              <div key={floor} style={{ marginBottom: 6 }}>
                <div style={S.floorLabel}>Floor {floor}</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${isMobile ? Math.min(rackCols, 4) : rackCols}, 1fr)`,
                    gap: 4,
                  }}
                >
                  {rooms.map(room => {
                    const status = resolveTileStatus(room);
                    const color = STATUS_COLOR[status] || STATUS_COLOR.VACANT;
                    const late = isLateCheckout(room);
                    return (
                      <div
                        key={room.room_id}
                        onClick={() => handleTileClick(room)}
                        title={room.primary_guest_name || room.room_type_name || ''}
                        style={{
                          height: tileH,
                          background: color.bg,
                          border: `2px solid ${late ? '#dc2626' : color.border}`,
                          borderRadius: 4,
                          padding: 3,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }}
                      >
                        {status === 'OCCUPIED' && (
                          <div style={{
                            background: color.border,
                            color: '#fff',
                            fontSize: tileFont - 1,
                            padding: '1px 3px',
                            borderRadius: 2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {shortGuestName(room.primary_guest_name)}
                          </div>
                        )}
                        <div style={{
                          fontWeight: 700,
                          fontSize: tileFont + 1,
                          color: color.text,
                          marginTop: status === 'OCCUPIED' ? 2 : 0,
                        }}>
                          {room.room_number}
                          {' '}
                          <span style={{ fontWeight: 400, fontSize: tileFont - 1 }}>
                            {room.room_type_code || room.room_type_name?.slice(0, 3).toUpperCase() || ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 2, marginTop: 'auto', fontSize: tileFont - 1 }}>
                          {room.channel_booking_id && <span title="OTA Booking">🌐</span>}
                          {late && <span title="Late checkout">⚠</span>}
                          {status === 'OCCUPIED' && room.actual_arrival_datetime && (
                            <span style={{ color: color.text, marginLeft: 'auto' }}>
                              {fmtDateTimeShort(room.actual_arrival_datetime)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div style={S.legend}>
            <Swatch c={STATUS_COLOR.VACANT.bg} b={STATUS_COLOR.VACANT.border} label="Vacant" />
            <Swatch c={STATUS_COLOR.OCCUPIED.bg} b={STATUS_COLOR.OCCUPIED.border} label="Occupied" />
            <Swatch c={STATUS_COLOR.DIRTY.bg} b={STATUS_COLOR.DIRTY.border} label="Dirty" />
            <Swatch c={STATUS_COLOR.RESERVED.bg} b={STATUS_COLOR.RESERVED.border} label="Reserved" />
            <Swatch c={STATUS_COLOR.BLOCKED.bg} b={STATUS_COLOR.BLOCKED.border} label="Blocked" />
            <Swatch c="#fff" b="#dc2626" label="Late" />
          </div>
        </div>

        {/* KPI + Debtor (right column) */}
        <div style={isMobile ? { ...S.kpiCol, width: '100%' } : S.kpiCol}>
          <div style={isMobile ? { ...S.kpiGrid, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : S.kpiGrid}>
            {kpiTiles.map(t => (
              <div key={t.label} style={S.kpiTile}>
                <div style={{ fontSize: tileNumberFont, fontWeight: 700, color: t.color }}>
                  {t.value}
                </div>
                <div style={{ fontSize: tileFont - 1, color: '#374151', textAlign: 'center' }}>
                  {t.label}
                </div>
              </div>
            ))}
          </div>
          <div style={S.debtorPanel}>
            <div style={S.panelHeader}>Debtors (In-House)</div>
            <div style={S.debtorBody}>
              <table style={S.debtorTable}>
                <thead>
                  <tr>
                    <th style={S.thLeft}>Debtor Name</th>
                    <th style={S.thRight}>Rooms</th>
                    <th style={S.thLeft}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {debtors.length === 0 && (
                    <tr><td colSpan={3} style={S.emptyCell}>—</td></tr>
                  )}
                  {debtors.slice(0, 5).map((d, i) => (
                    <tr key={i}>
                      <td style={{
                        ...S.tdLeft,
                        color: d.debtor_name === 'DIRECT' ? '#1d4ed8' : '#111827',
                      }}>{d.debtor_name}</td>
                      <td style={S.tdRight}>{d.rooms}</td>
                      <td style={S.tdLeft}>{d.room_type || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom half: 4 grids ── */}
      <div style={isMobile ? { ...S.bottomRow, gridTemplateColumns: '1fr', flex: 'none' } : S.bottomRow}>
        <GridPanel
          title="Room Continue"
          count={roomContinue.length}
          rows={roomContinue}
          fontSize={gridRowFont}
          onRowClick={(r) => onEditReservation?.(r.reservation_id)}
          columns={[
            { key: 'guest_name', label: 'Guest', flex: 2, render: (r) => r.guest_name },
            { key: 'checkin_datetime', label: 'CheckIn', flex: 2, render: (r) => fmtDateTimeShort(r.checkin_datetime) },
            { key: 'room_number', label: 'Rm', flex: 1, render: (r) => r.room_number },
            { key: 'tariff', label: 'Tariff', flex: 1, render: (r) => fmtMoney(r.tariff), align: 'right' },
            { key: 'debtor_name', label: 'Debtor', flex: 1, render: (r) => r.debtor_name },
          ]}
        />
        <GridPanel
          title="Yesterday Arrival"
          count={yesterdayArrivals.length}
          rows={yesterdayArrivals}
          fontSize={gridRowFont}
          onRowClick={(r) => onEditReservation?.(r.reservation_id)}
          columns={[
            { key: 'guest_name', label: 'Guest', flex: 2, render: (r) => r.guest_name },
            { key: 'checkin_datetime', label: 'CheckIn', flex: 2, render: (r) => fmtDateTimeShort(r.checkin_datetime) },
            { key: 'room_number', label: 'Rm', flex: 1, render: (r) => r.room_number },
            { key: 'tariff', label: 'Tariff', flex: 1, render: (r) => fmtMoney(r.tariff), align: 'right' },
          ]}
        />
        <GridPanel
          title="Today Arrival"
          count={todayArrivals.length}
          rows={todayArrivals}
          fontSize={gridRowFont}
          onRowClick={(r) => onEditReservation?.(r.reservation_id)}
          columns={[
            { key: 'guest_name', label: 'Guest', flex: 2, render: (r) => r.guest_name },
            { key: 'checkin_datetime', label: 'CheckIn', flex: 2, render: (r) => fmtDateTimeShort(r.checkin_datetime) },
            { key: 'room_number', label: 'Rm', flex: 1, render: (r) => r.room_number },
            { key: 'tariff', label: 'Tariff', flex: 1, render: (r) => fmtMoney(r.tariff), align: 'right' },
          ]}
        />
        <GridPanel
          title="Today Departure"
          count={todayDepartures.length}
          rows={todayDepartures}
          fontSize={gridRowFont}
          onRowClick={(r) => onEditReservation?.(r.reservation_id)}
          columns={[
            { key: 'room_number', label: 'Rm', flex: 1, render: (r) => r.room_number },
            { key: 'guest_name', label: 'Guest', flex: 2, render: (r) => r.guest_name },
            {
              key: 'checkout',
              label: 'Checkout',
              flex: 2,
              render: (r) => fmtDateTimeShort(r.actual_checkout_datetime || r.expected_checkout_datetime),
            },
            {
              key: 'dep_status',
              label: 'Status',
              flex: 1,
              render: (r) => (
                <span style={{
                  color: r.dep_status === 'DEPARTED' ? '#15803d' : '#b45309',
                  fontWeight: 600,
                }}>{r.dep_status}</span>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Swatch({ c, b, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{
        display: 'inline-block', width: 12, height: 12, background: c,
        border: `1.5px solid ${b}`, borderRadius: 2,
      }} />
      <span style={{ fontSize: 10, color: '#374151' }}>{label}</span>
    </span>
  );
}

function GridPanel({ title, count, rows, columns, fontSize, onRowClick }) {
  return (
    <div style={S.gridPanel}>
      <div style={S.panelHeader}>
        {title} <span style={{ color: '#6b7280', fontWeight: 400 }}>({count})</span>
      </div>
      <div style={S.gridBody}>
        <table style={{ ...S.gridTable, fontSize }}>
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} style={{
                  ...S.gridTh,
                  textAlign: c.align || 'left',
                  flex: c.flex,
                }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} style={S.emptyCell}>—</td></tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={r.stay_id || r.reservation_id || i}
                onClick={() => onRowClick?.(r)}
                style={S.gridTr}
              >
                {columns.map(c => (
                  <td key={c.key} style={{
                    ...S.gridTd,
                    textAlign: c.align || 'left',
                  }}>{c.render(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const S = {
  root: {
    height: 'calc(100vh - 80px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: '#f3f4f6',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  },
  banner: {
    height: 36,
    background: 'linear-gradient(90deg, #06b6d4, #3b82f6)',
    color: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 12px',
    flexShrink: 0,
  },
  search: {
    width: 220,
    height: 24,
    padding: '0 8px',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    fontSize: 12,
    background: '#fff',
    color: '#111827',
  },
  btn: {
    height: 24,
    padding: '0 10px',
    background: '#fff',
    color: '#111827',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    fontSize: 11,
    cursor: 'pointer',
  },
  btnPrimary: {
    height: 24,
    padding: '0 10px',
    background: '#1d4ed8',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 11,
    cursor: 'pointer',
    fontWeight: 600,
  },
  refreshHint: {
    color: '#e0f2fe',
    fontSize: 10,
    marginLeft: 6,
  },
  errorBar: {
    background: '#fee2e2',
    color: '#7f1d1d',
    padding: '4px 12px',
    fontSize: 12,
    flexShrink: 0,
  },

  topRow: {
    display: 'flex',
    gap: 6,
    padding: 6,
    flex: '0 0 48%',
    minHeight: 0,
  },
  rackPanel: {
    flex: '1 1 auto',
    background: '#fff',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  rackBody: {
    flex: 1,
    padding: 6,
    overflow: 'auto',
    minHeight: 0,
  },
  floorLabel: {
    fontSize: 10,
    color: '#6b7280',
    fontWeight: 600,
    marginBottom: 2,
    paddingLeft: 2,
  },
  legend: {
    display: 'flex',
    gap: 10,
    padding: '4px 8px',
    background: '#f9fafb',
    borderTop: '1px solid #e5e7eb',
    flexWrap: 'wrap',
  },

  kpiCol: {
    width: 320,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4,
    background: '#fff',
    padding: 6,
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    flex: '0 0 auto',
  },
  kpiTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    padding: '4px 2px',
    minHeight: 44,
  },
  debtorPanel: {
    background: '#fff',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  debtorBody: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  debtorTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 11,
  },
  thLeft: {
    textAlign: 'left',
    padding: '3px 6px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    fontWeight: 600,
  },
  thRight: {
    textAlign: 'right',
    padding: '3px 6px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    fontWeight: 600,
  },
  tdLeft: {
    textAlign: 'left',
    padding: '3px 6px',
    borderBottom: '1px solid #f3f4f6',
  },
  tdRight: {
    textAlign: 'right',
    padding: '3px 6px',
    borderBottom: '1px solid #f3f4f6',
    fontWeight: 600,
  },

  bottomRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 6,
    padding: '0 6px 6px 6px',
    flex: '1 1 auto',
    minHeight: 0,
  },
  gridPanel: {
    background: '#fff',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
  },
  gridBody: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  gridTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  gridTh: {
    padding: '4px 6px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    fontWeight: 600,
    fontSize: 11,
    color: '#374151',
  },
  gridTr: {
    cursor: 'pointer',
  },
  gridTd: {
    padding: '3px 6px',
    borderBottom: '1px solid #f3f4f6',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  panelHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: '#1f2937',
    padding: '4px 8px',
    background: '#eef2ff',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
  },
  emptyHint: {
    color: '#9ca3af',
    textAlign: 'center',
    padding: 20,
    fontSize: 12,
  },
  emptyCell: {
    color: '#9ca3af',
    textAlign: 'center',
    padding: 12,
    fontSize: 11,
  },
};

export default PmsFrontDeskBoard;
